import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, Linkedin, ArrowUpRight, Loader2 } from "lucide-react";
import PageHeader from "@/components/site/PageHeader";
import { COMPANY, OFFICES } from "@/constants/siteData";
import { submitContact } from "@/lib/api";

const initial = {
  name: "",
  email: "",
  company: "",
  phone: "",
  subject: "",
  message: "",
};

export default function ContactPage() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Message received — we'll be in touch shortly.");
      setForm(initial);
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Failed to submit message");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        eyebrow="C · Contact"
        title={<>Let&apos;s design the <span className="text-[#1E5AF6]">next chapter</span> of your business.</>}
        description="Whether you have a defined initiative or an idea worth exploring, our team is ready to partner with you. Share a few details and we'll respond within one business day."
        testId="contact-header"
      />

      <section className="py-20 md:py-28">
        <div className="container-page grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Details */}
          <aside className="lg:col-span-4 space-y-10">
            <div>
              <p className="eyebrow mb-4">Global offices</p>
              <p className="font-display text-2xl font-bold leading-snug">
                {COMPANY.legalName}
              </p>
              <ul className="mt-5 grid grid-cols-2 gap-3">
                {OFFICES.map((o) => (
                  <li
                    key={o.key}
                    data-testid={`contact-office-${o.key}`}
                    className="border border-[#e5e7eb] bg-white p-3"
                  >
                    <span
                      className="inline-block h-1.5 w-6 mb-2"
                      style={{ background: o.color }}
                    />
                    <p className="font-display font-bold text-sm leading-tight">
                      {o.city}
                    </p>
                    <p className="text-[11px] text-[#4B5563] leading-tight mt-0.5">
                      {o.country}
                    </p>
                    <p className="text-[10px] font-mono uppercase tracking-[0.16em] text-[#16A374] mt-1">
                      {o.label}
                    </p>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="eyebrow mb-4">Direct</p>
              <ul className="space-y-4">
                <li className="flex items-center gap-3 text-base">
                  <Phone className="w-4 h-4 text-[#1E5AF6]" />
                  <a
                    data-testid="contact-phone"
                    href={COMPANY.phoneHref}
                    className="link-underline"
                  >
                    {COMPANY.phone}
                  </a>
                </li>
                <li className="flex items-center gap-3 text-base">
                  <Mail className="w-4 h-4 text-[#1E5AF6]" />
                  <a
                    data-testid="contact-email"
                    href={`mailto:${COMPANY.email}`}
                    className="link-underline break-all"
                  >
                    {COMPANY.email}
                  </a>
                </li>
                <li className="flex items-center gap-3 text-base">
                  <Linkedin className="w-4 h-4 text-[#1E5AF6]" />
                  <a
                    data-testid="contact-linkedin"
                    href={COMPANY.linkedin}
                    target="_blank"
                    rel="noreferrer"
                    className="link-underline inline-flex items-center gap-1"
                  >
                    LinkedIn <ArrowUpRight className="w-3 h-3" />
                  </a>
                </li>
              </ul>
            </div>

            <div className="border-t border-[#e5e7eb] pt-8">
              <p className="eyebrow mb-3">Response window</p>
              <p className="text-sm text-[#4B5563] leading-relaxed">
                We aim to respond to every enquiry within one business day
                (Sunday–Thursday, GST).
              </p>
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-8">
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              noValidate
              className="border border-[#e5e7eb] p-6 md:p-10 bg-white"
            >
              <p className="eyebrow mb-2">01 · Enquiry form</p>
              <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight mb-8">
                Tell us about your initiative
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field
                  label="Full name"
                  required
                  value={form.name}
                  onChange={update("name")}
                  testId="contact-input-name"
                  autoComplete="name"
                />
                <Field
                  label="Work email"
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  testId="contact-input-email"
                  autoComplete="email"
                />
                <Field
                  label="Company"
                  value={form.company}
                  onChange={update("company")}
                  testId="contact-input-company"
                  autoComplete="organization"
                />
                <Field
                  label="Phone"
                  value={form.phone}
                  onChange={update("phone")}
                  testId="contact-input-phone"
                  autoComplete="tel"
                />
                <div className="md:col-span-2">
                  <Field
                    label="Subject"
                    required
                    value={form.subject}
                    onChange={update("subject")}
                    testId="contact-input-subject"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block eyebrow mb-3">Message *</label>
                  <textarea
                    data-testid="contact-input-message"
                    required
                    value={form.message}
                    onChange={update("message")}
                    rows={6}
                    placeholder="A few lines about goals, timelines, or constraints…"
                    className="w-full border border-[#e5e7eb] px-4 py-3 bg-white text-[#0B1220] placeholder:text-[#9ca3af] focus:outline-none focus:border-[#0B1220] transition-colors resize-none"
                  />
                </div>
              </div>

              <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                <p className="text-xs text-[#4B5563] max-w-md">
                  By submitting, you agree we may contact you regarding your
                  enquiry. We never share your details.
                </p>
                <button
                  data-testid="contact-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="group relative shine box-elevated inline-flex items-center gap-2 bg-[#0B1220] text-white px-7 py-4 text-sm font-semibold hover:bg-[#1E5AF6] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Send message
                      <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </>
                  )}
                </button>
              </div>

              {sent && (
                <p
                  data-testid="contact-success"
                  className="mt-6 text-sm text-[#1E5AF6] font-semibold"
                >
                  ✓ Message received. Our team will reach out shortly.
                </p>
              )}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, testId, ...rest }) {
  return (
    <label className="block">
      <span className="block eyebrow mb-3">
        {label} {required && "*"}
      </span>
      <input
        data-testid={testId}
        required={required}
        {...rest}
        className="w-full border border-[#e5e7eb] px-4 py-3 bg-white text-[#0B1220] placeholder:text-[#9ca3af] focus:outline-none focus:border-[#0B1220] transition-colors"
      />
    </label>
  );
}
